/** Official-portal guidelines + CHINTAMAT help form fields per financial-loss category. */

export const LOSS_CATEGORY_KEYS = [
  "trading",
  "consumer",
  "airline",
  "banking",
  "cyber",
  "insurance",
  "housing",
  "pension",
  "other",
];

const field = (key, label, opts = {}) => ({ key, label, required: false, ...opts });

export const lossCategoryGuides = {
  trading: {
    title: "Trading & Investment Fraud",
    authority: "SEBI SCORES / SMART ODR",
    portalUrl: "https://scores.sebi.gov.in",
    portalName: "SEBI SCORES",
    secondaryPortalUrl: "https://smartodr.in",
    secondaryPortalName: "SMART ODR",
    intro:
      "Use SEBI SCORES for complaints against SEBI-registered brokers, advisors, or research entities. For disputes with registered brokers or mutual funds, use SMART ODR.",
    steps: [
      "Collect screenshots of chats, payment receipts, contract notes, and Demat statements for the disputed period.",
      "Verify whether the entity is SEBI-registered at sebi.gov.in before filing.",
      "File at scores.sebi.gov.in — register, select the intermediary, attach evidence, and note your complaint reference number.",
      "For broker/MF disputes, also try smartodr.in if SCORES does not resolve the issue.",
      "If money moved via bank/UPI, file a parallel complaint at cybercrime.gov.in within 24 hours.",
    ],
    evidence: [
      "Telegram / WhatsApp chat exports",
      "UPI / bank payment receipts",
      "Broker contract notes & Demat statement",
      "Fake SEBI certificate screenshots",
      "Promised-return messages",
    ],
    formFields: [
      field("broker_name", "Broker / advisor / group name"),
      field("sebi_registration", "Claimed SEBI registration no. (if any)"),
      field("platform_used", "Platform (Telegram, WhatsApp, app, website)"),
      field("demat_account", "Demat / trading account no. (if any)"),
      field("official_ref", "SEBI SCORES / SMART ODR ref. no. (if already filed)"),
      field("incident_dates", "Approximate dates of loss"),
    ],
  },
  banking: {
    title: "Banking, UPI, Card & Loan",
    authority: "RBI",
    portalUrl: "https://cms.rbi.org.in",
    portalName: "RBI CMS",
    intro:
      "RBI's Complaint Management System (CMS) handles grievances against banks, NBFCs, payment banks, and digital lenders. For urgent unauthorised transactions, also call your bank's 24×7 helpline immediately.",
    steps: [
      "Block your card / UPI / net-banking immediately if fraud is ongoing.",
      "Download bank statements highlighting every disputed transaction.",
      "Raise a written complaint with your bank branch or app — keep the complaint reference number.",
      "If unresolved within 30 days, escalate at cms.rbi.org.in with your bank complaint ref. and evidence.",
      "For loan/NBFC disputes, select the correct entity type in RBI CMS and attach loan agreement copies.",
    ],
    evidence: [
      "Bank / UPI transaction SMS & statements",
      "Bank complaint acknowledgement",
      "Loan agreement & EMI schedule",
      "Screenshots of unauthorised debits",
    ],
    formFields: [
      field("bank_name", "Bank / NBFC / lender name"),
      field("account_or_upi", "Account no. / UPI ID / card last 4 digits"),
      field("transaction_ids", "Transaction ID(s) or UTR numbers"),
      field("transaction_date", "Date(s) of disputed transaction(s)"),
      field("issue_type", "Issue type (unauthorised debit, UPI fraud, card misuse, loan dispute)", {
        required: true,
        type: "select",
        options: ["Unauthorised debit", "UPI fraud", "Card misuse", "Loan / NBFC dispute", "Other"],
      }),
      field("bank_complaint_ref", "Bank complaint reference no. (if any)"),
    ],
  },
  cyber: {
    title: "Cyber Fraud & Scam Payments",
    authority: "National Cybercrime Portal / 1930",
    portalUrl: "https://cybercrime.gov.in",
    portalName: "Cybercrime Portal",
    intro:
      "Report all online financial fraud at cybercrime.gov.in or call the national helpline 1930. Time matters — reporting within 24 hours improves the chance of payment freeze.",
    steps: [
      "Call 1930 immediately and note the reference given by the operator.",
      "Do not delete chats, emails, or payment screenshots — export them now.",
      "File at cybercrime.gov.in → Report Other Cyber Crime → Financial Fraud.",
      "Attach UPI/bank receipts, scammer phone/UPI ID, and full chat history.",
      "Take the acknowledgement PDF and share it with your bank for a fraud dispute.",
    ],
    evidence: [
      "UPI / bank transfer receipts with UTR",
      "Scammer phone, UPI ID, or wallet address",
      "Full chat / email export",
      "Cybercrime portal acknowledgement",
    ],
    formFields: [
      field("incident_date", "Date of fraud"),
      field("payment_method", "Payment method (UPI, bank transfer, wallet, crypto)"),
      field("scammer_contact", "Scammer phone / UPI ID / email / website"),
      field("cyber_ref", "Cybercrime portal / 1930 reference no. (if filed)"),
      field("amount_transferred", "Total amount transferred (INR)", { type: "number" }),
      field("bank_notified", "Did you notify your bank?", {
        type: "select",
        options: ["Yes", "No", "In progress"],
      }),
    ],
  },
  insurance: {
    title: "Insurance Claim Disputes",
    authority: "IRDAI",
    portalUrl: "https://bimabharosa.irdai.gov.in",
    portalName: "IRDAI Bima Bharosa",
    intro:
      "IRDAI's Bima Bharosa portal handles health, life, motor, and general insurance grievances against insurers and agents.",
    steps: [
      "First lodge a written complaint with the insurer's customer care — email or portal — and save the ticket number.",
      "Gather policy document, claim form, rejection letter, and medical/bill records.",
      "If not resolved in 15 days, file at bimabharosa.irdai.gov.in with insurer complaint ref.",
      "Select correct policy type and attach all supporting documents as PDF.",
      "Keep tracking the grievance ID until IRDAI closes the case.",
    ],
    evidence: [
      "Policy copy & premium receipts",
      "Claim form & rejection letter",
      "Hospital / repair bills",
      "Insurer email trail",
    ],
    formFields: [
      field("insurer_name", "Insurance company name"),
      field("policy_number", "Policy number"),
      field("claim_number", "Claim number (if any)"),
      field("issue_type", "Issue type", {
        type: "select",
        options: ["Claim rejected", "Delayed settlement", "Mis-selling", "Partial payment", "Other"],
      }),
      field("irdai_ref", "Bima Bharosa grievance ID (if filed)"),
    ],
  },
  airline: {
    title: "Airline Refund & Delay",
    authority: "AirSewa / DGCA",
    portalUrl: "https://airsewa.gov.in",
    portalName: "AirSewa",
    intro:
      "AirSewa is DGCA's official portal for airline refunds, delays, cancellations, and baggage grievances on Indian and international flights to/from India.",
    steps: [
      "Raise a complaint directly with the airline via email/app first — note the PNR and complaint ID.",
      "For refunds: cite DGCA circular on refund timelines if the airline delays beyond 7 working days.",
      "For baggage: file a Property Irregularity Report (PIR) at the airport before leaving.",
      "If unresolved, lodge at airsewa.gov.in with PNR, ticket copy, and airline response.",
      "Attach boarding pass, baggage tag, and delay/cancellation proof.",
    ],
    evidence: [
      "Ticket & boarding pass",
      "PNR & airline complaint ID",
      "PIR for baggage issues",
      "Delay / cancellation SMS or email",
    ],
    formFields: [
      field("airline_name", "Airline name"),
      field("pnr", "PNR / booking reference"),
      field("flight_number", "Flight number & date"),
      field("issue_type", "Issue type", {
        type: "select",
        options: ["Refund not received", "Flight delay", "Cancellation", "Lost / damaged baggage", "Other"],
      }),
      field("airsewa_ref", "AirSewa complaint ID (if filed)"),
    ],
  },
  consumer: {
    title: "Consumer Product & Service",
    authority: "National Consumer Helpline",
    portalUrl: "https://consumerhelpline.gov.in",
    portalName: "National Consumer Helpline",
    intro:
      "Covers both online shopping (e-commerce sites like Amazon, Flipkart, Meesho) and offline purchases (local shops, showrooms, service providers). Use the National Consumer Helpline and e-daakhil for defective products, missing orders, refund denials, and unfair trade practices.",
    steps: [
      "Contact the seller/platform customer care and save ticket ID, chat, and order confirmation.",
      "Take photos/videos of the defective product or wrong item received.",
      "Call 1800-11-4000 or register at consumerhelpline.gov.in with order details.",
      "If unresolved, file at edaakhil.nic.in (Consumer Commission online filing).",
      "Keep invoice, warranty card, and all communication as evidence.",
    ],
    evidence: [
      "Order ID & invoice",
      "Product photos / unboxing video",
      "Customer care chat / email",
      "Return pickup refusal proof",
    ],
    formFields: [
      field("platform_name", "Platform / store name (Amazon, Flipkart, local shop, etc.)"),
      field("order_id", "Order / invoice number"),
      field("product_service", "Product or service name"),
      field("issue_type", "Issue type", {
        type: "select",
        options: ["Wrong product", "Defective product", "Refund denied", "Missing item", "Warranty dispute", "Other"],
      }),
      field("consumer_ref", "Consumer helpline / e-daakhil ref. (if filed)"),
    ],
  },
  housing: {
    title: "Housing Finance Complaints",
    authority: "NHB",
    portalUrl: "https://nhb.org.in",
    portalName: "NHB",
    intro:
      "The National Housing Bank supervises housing-finance companies (HFCs). Escalate home-loan grievances here after raising a complaint with your lender.",
    steps: [
      "Submit a written complaint to your HFC / bank's grievance redressal officer.",
      "Collect loan sanction letter, EMI schedule, and correspondence on rate changes or charges.",
      "If unresolved in 30 days, file with NHB against the housing-finance company.",
      "Attach loan account number, dispute summary, and lender's response.",
      "For co-operative bank home loans, RBI CMS may apply instead — check entity type.",
    ],
    evidence: [
      "Loan sanction & agreement",
      "EMI payment receipts",
      "Lender grievance acknowledgement",
      "Rate / charge dispute emails",
    ],
    formFields: [
      field("lender_name", "Housing finance company / bank name"),
      field("loan_account", "Loan account number"),
      field("issue_type", "Issue type", {
        type: "select",
        options: ["Hidden charges", "Rate dispute", "Foreclosure issue", "Document delay", "Harassment", "Other"],
      }),
      field("nhb_ref", "NHB complaint reference (if filed)"),
    ],
  },
  pension: {
    title: "Pension Grievances",
    authority: "PFRDA",
    portalUrl: "https://www.pfrda.org.in",
    portalName: "PFRDA",
    intro:
      "PFRDA handles NPS and pension-fund grievances. EPFO matters go to epfindia.gov.in — note which applies to your case.",
    steps: [
      "Identify your pension system: NPS (PRAN), EPFO (UAN), or employer pension scheme.",
      "For NPS: raise grievance at nps.kfintech.com or through your Point of Presence (POP).",
      "If unresolved, escalate to PFRDA grievance cell with PRAN and transaction details.",
      "For EPFO: use the EPFO member portal → Grievance → register with UAN.",
      "Attach PRAN/UAN, contribution statements, and pension statement copies.",
    ],
    evidence: [
      "PRAN / UAN card",
      "Contribution statements",
      "Pension / withdrawal rejection letter",
      "Employer correspondence",
    ],
    formFields: [
      field("pension_type", "Pension type", {
        type: "select",
        options: ["NPS", "EPFO", "Employer pension", "Other"],
      }),
      field("pran_or_uan", "PRAN / UAN / account number"),
      field("issue_type", "Issue type", {
        type: "select",
        options: ["Withdrawal rejected", "Wrong deduction", "Account transfer issue", "Pension delay", "Other"],
      }),
      field("pfrda_ref", "PFRDA / EPFO grievance ID (if filed)"),
    ],
  },
  other: {
    title: "Other Financial Loss",
    authority: "National Consumer Helpline / Cybercrime Portal",
    portalUrl: "https://consumerhelpline.gov.in",
    portalName: "National Consumer Helpline",
    secondaryPortalUrl: "https://cybercrime.gov.in",
    secondaryPortalName: "Cybercrime Portal",
    intro:
      "If your loss does not fit the listed categories — chit-fund fraud, MLM schemes, crypto wallet scams, job fraud, loan-app harassment, or any other financial dispute — start here. We help you identify the right authority and document your case.",
    steps: [
      "Write a one-page timeline: dates, amounts, who you paid, and what was promised.",
      "Collect every receipt, chat export, email, and reference number you have.",
      "If money was transferred online, file at cybercrime.gov.in within 24 hours (helpline 1930).",
      "Identify the regulator: banking → RBI CMS, investment → SEBI SCORES, insurance → IRDAI, service dispute → Consumer Helpline.",
      "Submit to CHINTAMAT below — our team will help route you to the correct forum.",
    ],
    evidence: [
      "Payment receipts & bank/UPI statements",
      "Chat / email / call recordings",
      "Contract or agreement copies",
      "Any complaint reference numbers already filed",
    ],
    formFields: [
      field("loss_description_short", "Brief type of loss (e.g. chit fund, job fraud, crypto)"),
      field("entity_name", "Company / person / app name involved"),
      field("amount_lost_detail", "Approximate amount lost (INR)"),
      field("official_ref", "Any complaint ref. no. already filed"),
      field("preferred_authority", "Authority you think applies (if known)"),
    ],
  },
};

export function getLossGuide(categoryKey) {
  return lossCategoryGuides[categoryKey] || null;
}
